import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { LogOut, Plus, TrendingUp, DollarSign, ShoppingBag, Bell, Bluetooth, Printer } from 'lucide-react';
import io from 'socket.io-client';
import api from '../utils/api';
import { formatCurrency, formatDate } from '../utils/format';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { getThermalPrinter, isBluetoothSupported } from '../utils/bluetoothPrinter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SOCKET_PATH = "/api/socket.io";

const VendedorDashboard = () => {
  const { user, logout } = useAuth();
  const { permission, requestPermission, isSupported } = usePushNotifications();
  const socketRef = useRef(null);
  const [ventas, setVentas] = useState([]);
  const [nichos, setNichos] = useState([]);
  const [stats, setStats] = useState({ total_sales: 0, total_commission: 0, count: 0 });
  const [statsByNicho, setStatsByNicho] = useState([]);
  const [ventaDialogOpen, setVentaDialogOpen] = useState(false);
  
  // Estado para impresora Bluetooth
  const [bluetoothPrinter, setBluetoothPrinter] = useState(null);
  const [printerConnected, setPrinterConnected] = useState(false);
  const [printerName, setPrinterName] = useState('');
  const [autoReconnecting, setAutoReconnecting] = useState(false);
  
  // Cargar nombre de impresora desde localStorage al montar
  useEffect(() => {
    const savedPrinterName = localStorage.getItem('printer_name');
    if (savedPrinterName) {
      setPrinterName(savedPrinterName);
    }
  }, []);
  
  // Mantener la conexión activa
  useEffect(() => {
    if (bluetoothPrinter && printerConnected) {
      // Verificar conexión cada 30 segundos
      const interval = setInterval(() => {
        if (bluetoothPrinter.device?.gatt?.connected === false) {
          console.log('⚠️ Impresora desconectada, intentando reconectar...');
          setPrinterConnected(false);
        }
      }, 30000);
      
      return () => clearInterval(interval);
    }
  }, [bluetoothPrinter, printerConnected]);
  
  // Estado para múltiples productos
  const [currentItems, setCurrentItems] = useState([]);
  const [currentItem, setCurrentItem] = useState({
    nicho_id: '',
    product_name: '',
    amount: ''
  });
  const [paymentMethod, setPaymentMethod] = useState("EF");
  
  // Legacy support - mantener por compatibilidad
  const [newVenta, setNewVenta] = useState({
    nicho_id: '',
    product_name: '',
    amount: ''
  });

  useEffect(() => {
    fetchData();
    initializeWebSocket();
    
    // Intentar auto-reconexión de Bluetooth
    attemptBluetoothAutoReconnect();

    return () => {
      if (socketRef.current?.connected) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  const initializeWebSocket = () => {
    socketRef.current = io(BACKEND_URL, { path: SOCKET_PATH });

    socketRef.current.on('connect', () => {
      console.log('WebSocket connected');
      socketRef.current.emit('join_vendedor_room', { vendedor_id: user?.id });
    });

    socketRef.current.on('vendedor_liquidado', (data) => {
      console.log('🔔 Vendedor liquidado:', data);
      if (data.vendedor_id === user?.id) {
        toast.info('Tu cuenta ha sido liquidada. Actualizando datos...');
        // Refrescar todos los datos
        fetchData();
      }
    });
    socketRef.current.on('sale_deleted', (data) => {
      console.log('🗑️ Venta eliminada:', data);
      if (data.vendedor_id === user?.id) {
        fetchData();
      }
    });

    socketRef.current.on('disconnect', () => {
      console.log('WebSocket disconnected');
    });
  };

  const fetchData = async () => {
    await Promise.all([
      fetchVentas(),
      fetchNichos(),
      fetchStats()
    ]);
  };

  const fetchVentas = async () => {
    try {
      const { data } = await api.get('/api/ventas');
      setVentas(data);
    } catch (error) {
      console.error('Error fetching ventas:', error);
    }
  };

  const fetchNichos = async () => {
    try {
      const { data } = await api.get('/api/nichos');
      setNichos(data);
    } catch (error) {
      console.error('Error fetching nichos:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const [generalStats, nichoStats] = await Promise.all([
        api.get('/api/ventas/stats'),
        api.get('/api/ventas/stats/by-nicho')
      ]);
      setStats(generalStats.data);
      setStatsByNicho(nichoStats.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleAddItem = () => {
    if (!currentItem.nicho_id || !currentItem.product_name || !currentItem.amount) {
      toast.error('Complete todos los campos del producto');
      return;
    }
    
    const nicho = nichos.find(n => n.id === currentItem.nicho_id);
    const newItem = {
      nicho_id: currentItem.nicho_id,
      nicho_name: nicho?.name || '',
      product_name: currentItem.product_name,
      amount: parseFloat(currentItem.amount)
    };
    
    setCurrentItems([...currentItems, newItem]);
    setCurrentItem({ nicho_id: '', product_name: '', amount: '' });
      setPaymentMethod("EF");
    toast.success('Producto agregado');
  };
  
  const handleRemoveItem = (index) => {
    setCurrentItems(currentItems.filter((_, i) => i !== index));
    toast.info('Producto eliminado');
  };
  
  const calculateTotal = () => {
    return currentItems.reduce((sum, item) => sum + item.amount, 0);
  };
  
  // Conectar impresora Bluetooth
  const handleConnectPrinter = async () => {
    try {
      if (!isBluetoothSupported()) {
        toast.error('Tu navegador no soporta Bluetooth. Usa Chrome o Edge.');
        return;
      }

      const printer = getThermalPrinter();
      
      // Intentar conectar específicamente a 58HB6 primero
      console.log('🔍 Buscando impresora 58HB6...');
      toast.info('Buscando impresora 58HB6...', { duration: 2000 });
      
      let result;
      try {
        result = await printer.connectToSpecificPrinter('58HB6');
      } catch (error) {
        // Si falla, intentar conexión genérica
        console.log('⚠️ No se encontró 58HB6, mostrando todas las impresoras...');
        result = await printer.connect();
      }
      
      if (result.success) {
        setBluetoothPrinter(printer);
        setPrinterConnected(true);
        setPrinterName(result.deviceName);
        localStorage.setItem('printer_name', result.deviceName);
        toast.success(`✅ Impresora conectada: ${result.deviceName}`, {
          duration: 4000
        });
        
        // Listener para detectar desconexión
        if (printer.device) {
          printer.device.addEventListener('gattserverdisconnected', () => {
            console.log('🔌 Impresora desconectada');
            setPrinterConnected(false);
            toast.warning('Impresora desconectada. Refresca para reconectar.');
          });
        }
      }
    } catch (error) {
      console.error('Error conectando impresora:', error);
      if (error.message.includes('User cancelled')) {
        toast.info('Conexión cancelada');
      } else {
        toast.error(error.message || 'No se pudo conectar a la impresora');
      }
    }
  };

  // Desconectar impresora
  const handleDisconnectPrinter = () => {
    if (bluetoothPrinter) {
      bluetoothPrinter.disconnect();
      setBluetoothPrinter(null);
      setPrinterConnected(false);
      // No borrar el nombre de localStorage para recordar cuál usar
      toast.info('Impresora desconectada');
    }
  };

  // Intentar auto-reconexión al cargar la página
  const attemptBluetoothAutoReconnect = async () => {
    try {
      if (!isBluetoothSupported()) {
        console.log('⚠️ Bluetooth no soportado en este navegador');
        return;
      }

      console.log('🔄 Intentando conectar a impresora 58HB6...');
      setAutoReconnecting(true);

      const printer = getThermalPrinter();
      // Intentar reconectar a 58HB6 específicamente
      const result = await printer.autoReconnect('58HB6');

      if (result.success) {
        setBluetoothPrinter(printer);
        setPrinterConnected(true);
        setPrinterName(result.deviceName);
        toast.success(`✅ Impresora conectada: ${result.deviceName}`, {
          duration: 4000
        });

        // Listener para detectar desconexión
        if (printer.device) {
          printer.device.addEventListener('gattserverdisconnected', () => {
            console.log('🔌 Impresora desconectada');
            setPrinterConnected(false);
            toast.warning('Impresora desconectada. Refresca para reconectar.');
          });
        }
      } else {
        console.log(`ℹ️ Auto-reconexión no disponible: ${result.reason}`);
        
        // Mostrar mensaje más claro según la razón
        if (result.reason === 'no_paired_devices') {
          console.log('💡 Primera vez: Click en el botón Bluetooth para emparejar tu impresora');
        } else if (result.reason === 'api_not_available') {
          console.log('💡 Navegador sin soporte: Click en el botón Bluetooth para conectar');
        }
      }
    } catch (error) {
      console.error('Error en auto-reconexión:', error);
    } finally {
      setAutoReconnecting(false);
    }
  };

  // Imprimir vía Bluetooth
  const handleBluetoothPrint = async (ventaId) => {
    try {
      if (!printerConnected || !bluetoothPrinter) {
        toast.error('Conecta primero la impresora Bluetooth');
        return;
      }

      // Obtener datos de la venta desde el backend
      const { data: venta } = await api.get(`/api/ventas/${ventaId}`);
      
      // Preparar datos del ticket usando la fecha formateada del backend
      const ticketData = {
        ticket_number: venta.ticket_number || 'N/A',
        fecha: venta.fecha_formatted || formatDate(venta.created_at),
        vendedor_name: user.name,
        items: venta.items || [
          {
            product_name: venta.product_name,
            amount: venta.amount,
            nicho_name: venta.nicho_name
          }
        ],
        total_amount: venta.total_amount || venta.amount
      };

      toast.info('Imprimiendo...');
      await bluetoothPrinter.printTicket(ticketData);
      toast.success('✓ Ticket impreso correctamente');
      
    } catch (error) {
      console.error('Error imprimiendo:', error);
      toast.error('Error al imprimir: ' + (error.message || 'Revisa la conexión'));
    }
  };

  const handleReprintTicket = async (ventaId) => {
    try {
      if (printerConnected && bluetoothPrinter) {
        const useBluetooth = window.confirm('¿Imprimir con impresora térmica Bluetooth?\n\nOK = Térmica\nCancelar = Normal');
        if (useBluetooth) {
          await handleBluetoothPrint(ventaId);
        } else {
          const ticketUrl = `${BACKEND_URL}/api/ventas/${ventaId}/ticket`;
          window.open(ticketUrl, '_blank', 'width=800,height=600');
        }
      } else {
        const ticketUrl = `${BACKEND_URL}/api/ventas/${ventaId}/ticket`;
        const printWindow = window.open(ticketUrl, '_blank', 'width=800,height=600');
        if (!printWindow) {
          toast.error('Por favor permite ventanas emergentes para imprimir');
        }
      }
    } catch (error) {
      toast.error('Error al reimprimir: ' + error.message);
    }
  };
  
  const handleCreateVenta = async (shouldPrint = false, useBluetooth = false) => {
    try {
      if (currentItems.length === 0) {
        toast.error('Agregue al menos un producto');
        return;
      }
      
      const response = await api.post('/api/ventas', {
        items: currentItems,
        payment_method: paymentMethod
      });
      
      toast.success('Venta registrada exitosamente');
      
      // Reset form and close modal FIRST
      setCurrentItems([]);
      setCurrentItem({ nicho_id: '', product_name: '', amount: '' });
      setPaymentMethod("EF");
      setVentaDialogOpen(false);
      fetchData();
      
      // Then handle printing
      if (shouldPrint && response.data.id) {
        if (useBluetooth && printerConnected) {
          // Imprimir vía Bluetooth
          await handleBluetoothPrint(response.data.id);
        } else {
          // Imprimir tradicional (window.print)
          setTimeout(() => {
            const ticketUrl = `${BACKEND_URL}/api/ventas/${response.data.id}/ticket`;
            const printWindow = window.open(ticketUrl, '_blank', 'width=800,height=600');
            if (!printWindow) {
              toast.error('Por favor permite ventanas emergentes para imprimir');
            }
          }, 300);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar venta');
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b]">
      {/* Header */}
      <header className="bg-[#18181b] border-b border-[#27272a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-[#fafafa] tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                BuenasCompras END
              </h1>
              <p className="text-[#a1a1aa] text-sm mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Panel de Vendedor
              </p>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-[#a1a1aa] text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {user?.name}
              </span>
              
              {/* Bluetooth Printer */}
              {isBluetoothSupported() && (
                <Button
                  onClick={printerConnected ? handleDisconnectPrinter : handleConnectPrinter}
                  variant="outline"
                  size="sm"
                  className={
                    autoReconnecting 
                      ? "border-[#eab308] text-[#eab308] hover:bg-[#eab308]/10"
                      : printerConnected 
                        ? "border-[#10b981] text-[#10b981] hover:bg-[#10b981]/10" 
                        : "border-[#27272a] text-[#fafafa] hover:bg-[#27272a]"
                  }
                  title={
                    autoReconnecting 
                      ? "Reconectando..."
                      : printerConnected 
                        ? `Conectado: ${printerName}` 
                        : "Conectar impresora Bluetooth"
                  }
                  disabled={autoReconnecting}
                >
                  <Bluetooth className="w-4 h-4" />
                  {autoReconnecting && <span className="ml-1 text-xs">🔄</span>}
                  {printerConnected && <span className="ml-1 hidden sm:inline text-xs">✓</span>}
                </Button>
              )}
              
              {/* Push Notifications */}
              {isSupported && permission === 'default' && (
                <Button
                  onClick={requestPermission}
                  variant="outline"
                  size="sm"
                  className="border-[#10b981] text-[#10b981] hover:bg-[#10b981]/10"
                  title="Activar notificaciones push"
                >
                  <Bell className="w-4 h-4" />
                </Button>
              )}
              
              <Button
                data-testid="logout-button"
                onClick={logout}
                variant="outline"
                className="border-[#27272a] text-[#fafafa] hover:bg-[#27272a]"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Salir
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-[#18181b] border-[#27272a] p-6">
              <div className="flex items-center gap-3 mb-2">
                <ShoppingBag className="w-5 h-5 text-[#2563eb]" />
                <div className="text-[#a1a1aa] text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>Total Ventas</div>
              </div>
              <div className="text-3xl font-bold text-[#fafafa] tabular-nums" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                {formatCurrency(stats.total_sales)}
              </div>
              <div className="text-[#a1a1aa] text-xs mt-1">{stats.count} ventas</div>
            </Card>
            <Card className="bg-[#18181b] border-[#27272a] p-6">
              <div className="flex items-center gap-3 mb-2">
                <DollarSign className="w-5 h-5 text-[#10b981]" />
                <div className="text-[#a1a1aa] text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>Comisiones Ganadas</div>
              </div>
              <div className="text-3xl font-bold text-[#10b981] tabular-nums" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                {formatCurrency(stats.total_commission)}
              </div>
            </Card>
            <Card className="bg-[#18181b] border-[#27272a] p-6">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="w-5 h-5 text-[#f59e0b]" />
                <div className="text-[#a1a1aa] text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>Promedio por Venta</div>
              </div>
              <div className="text-3xl font-bold text-[#fafafa] tabular-nums" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                {formatCurrency(stats.count > 0 ? stats.total_sales / stats.count : 0)}
              </div>
            </Card>
          </div>

          {/* Nueva Venta Button */}
          <div>
            <Dialog open={ventaDialogOpen} onOpenChange={setVentaDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="create-venta-button" className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white">
                  <Plus className="w-4 h-4 mr-2" />
                  Registrar Nueva Venta
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#18181b] border-[#27272a] text-[#fafafa] max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Registrar Nueva Venta</DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4">
                  {/* Formulario para agregar producto */}
                  <div className="space-y-3 p-4 bg-[#09090b] border border-[#27272a] rounded">
                    <div>
                      <Label htmlFor="venta-nicho">Nicho</Label>
                      <select
                        id="venta-nicho"
                        value={currentItem.nicho_id}
                        onChange={(e) => setCurrentItem({ ...currentItem, nicho_id: e.target.value })}
                        className="w-full p-2 bg-[#18181b] border border-[#27272a] text-[#fafafa] rounded"
                      >
                        <option value="">Seleccionar nicho</option>
                        {nichos.map((nicho) => (
                          <option key={nicho.id} value={nicho.id}>{nicho.name}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <Label htmlFor="venta-product">Producto</Label>
                      <Input
                        id="venta-product"
                        value={currentItem.product_name}
                        onChange={(e) => setCurrentItem({ ...currentItem, product_name: e.target.value })}
                        className="bg-[#18181b] border-[#27272a] text-[#fafafa]"
                        placeholder="Nombre del producto"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="venta-amount">Precio (ARS)</Label>
                      <Input
                        id="venta-amount"
                        type="number"
                        step="0.01"
                        min="0"
                        value={currentItem.amount}
                        onChange={(e) => setCurrentItem({ ...currentItem, amount: e.target.value })}
                        className="bg-[#18181b] border-[#27272a] text-[#fafafa]"
                        placeholder="0.00"
                      />
                    </div>
                    
                    <Button 
                      onClick={handleAddItem}
                      type="button"
                      className="w-full bg-[#10b981] hover:bg-[#059669] text-white"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Agregar Producto
                    </Button>
                  </div>
                  
                  {/* Lista de productos agregados */}
                  {currentItems.length > 0 && (
                    <div className="space-y-2">
                      <Label>Productos a registrar ({currentItems.length})</Label>
                      <div className="max-h-60 overflow-y-auto space-y-2">
                        {currentItems.map((item, index) => (
                          <div key={index} className="flex justify-between items-center p-3 bg-[#09090b] border border-[#27272a] rounded">
                            <div className="flex-1">
                              <div className="text-[#fafafa] font-medium">{item.product_name}</div>
                              <div className="text-[#71717a] text-sm">{item.nicho_name}</div>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-[#10b981] font-bold">{formatCurrency(item.amount)}</span>
                              <Button
                                onClick={() => handleRemoveItem(index)}
                                variant="ghost"
                                size="sm"
                                className="text-[#ef4444] hover:bg-[#ef4444]/10"
                              >
                                ✕
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Total */}
                      <div className="flex justify-between items-center p-3 bg-[#18181b] border border-[#27272a] rounded font-bold">
                        <span className="text-[#fafafa]">TOTAL:</span>
                        <span className="text-[#10b981] text-lg">{formatCurrency(calculateTotal())}</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Método de Pago */}
                  {currentItems.length > 0 && (
                    <div className="flex items-center gap-2 px-4">
                      <Label className="text-sm">Pago:</Label>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setPaymentMethod("EF")}
                          className={`px-3 py-1.5 rounded border text-sm font-medium transition-all ${paymentMethod === "EF" ? 'bg-[#2563eb] border-[#2563eb] text-white' : 'bg-[#18181b] border-[#27272a] text-[#a1a1aa] hover:border-[#3f3f46]'}`}
                        >
                          💵 EF
                        </button>
                        <button
                          type="button"
                          onClick={() => setPaymentMethod("MP")}
                          className={`px-3 py-1.5 rounded border text-sm font-medium transition-all ${paymentMethod === "MP" ? 'bg-[#00b0ff] border-[#00b0ff] text-white' : 'bg-[#18181b] border-[#27272a] text-[#a1a1aa] hover:border-[#3f3f46]'}`}
                        >
                          💳 MP
                        </button>
                      </div>
                    </div>
                  )}
                  {/* Botones de acción */}
                  <div className="flex flex-col gap-2">
                    <div className="flex gap-3">
                      <Button
                        onClick={() => handleCreateVenta(false)}
                        disabled={currentItems.length === 0}
                        className="flex-1 bg-[#2563eb] hover:bg-[#1d4ed8] text-white"
                      >
                        Registrar Venta
                      </Button>
                      {printerConnected ? (
                        <Button 
                          onClick={() => handleCreateVenta(true, true)}
                          disabled={currentItems.length === 0}
                          className="flex-1 bg-[#10b981] hover:bg-[#059669] text-white"
                        >
                          <Bluetooth className="w-4 h-4 mr-2" />
                          Imprimir Térmica
                        </Button>
                      ) : (
                        <Button 
                          onClick={() => handleCreateVenta(true, false)}
                          disabled={currentItems.length === 0}
                          className="flex-1 bg-[#8b5cf6] hover:bg-[#7c3aed] text-white"
                        >
                          <Printer className="w-4 h-4 mr-2" />
                          Imprimir Normal
                        </Button>
                      )}
                    </div>
                    
                    {/* Mensaje de ayuda para Bluetooth */}
                    {!printerConnected && isBluetoothSupported() && (
                      <p className="text-[#71717a] text-xs text-center">
                        💡 Conecta tu impresora Bluetooth desde el botón en la esquina superior
                      </p>
                    )}
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Stats by Nicho */}
          <Card className="bg-[#18181b] border-[#27272a] p-6">
            <h3 className="text-xl font-bold text-[#fafafa] mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Ventas por Nicho
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {statsByNicho.map((stat) => (
                <div key={stat.nicho_id} className="p-4 bg-[#09090b] border border-[#27272a] rounded">
                  <div className="text-[#fafafa] font-bold text-lg mb-1">{stat.nicho_name}</div>
                  <div className="text-[#a1a1aa] text-sm mb-2">{stat.count} ventas</div>
                  <div className="text-[#fafafa] font-bold tabular-nums">{formatCurrency(stat.total_sales)}</div>
                  <div className="text-[#10b981] text-sm font-bold tabular-nums">+{formatCurrency(stat.total_commission)}</div>
                </div>
              ))}
              {statsByNicho.length === 0 && (
                <div className="col-span-3 text-center text-[#a1a1aa] py-8">
                  No hay ventas registradas aún
                </div>
              )}
            </div>
          </Card>

          {/* Historial de Ventas */}
          <Card className="bg-[#18181b] border-[#27272a] p-6">
            <h3 className="text-xl font-bold text-[#fafafa] mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Historial de Ventas
            </h3>
            <div data-testid="ventas-list" className="space-y-2 max-h-96 overflow-y-auto">
              {ventas.length === 0 ? (
                <div className="text-[#a1a1aa] text-center py-8">No hay ventas registradas</div>
              ) : (
                ventas.map((venta) => (
                  <div
                    key={venta.id}
                    data-testid="venta-item"
                    className="flex justify-between items-center p-4 bg-[#09090b] border border-[#27272a] rounded hover:border-[#2563eb] transition-colors"
                  >
                    <div className="flex-1">
                      <div className="text-[#fafafa] font-medium">{venta.product_name}</div>
                      <div className="text-[#a1a1aa] text-sm">
                        {venta.nicho_name} • {formatDate(venta.created_at)}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => handleReprintTicket(venta.id)}
                        className="p-2 rounded hover:bg-[#27272a] text-[#a1a1aa] hover:text-[#fafafa] transition-colors"
                        title="Reimprimir ticket"
                      >
                        🖨️
                      </button>
                      <div className="text-right">
                        <div className="text-[#fafafa] font-bold tabular-nums">{formatCurrency(venta.amount)}</div>
                        <div className="text-[#10b981] text-sm tabular-nums">+{formatCurrency(venta.commission_earned)}</div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default VendedorDashboard;
